import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./Admin.css";
import { FEATURE_OPTIONS } from "./mock/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map feature keys -> readable German labels for the dashboard.
const FEATURE_LABELS = FEATURE_OPTIONS.de.reduce((acc, o) => {
  acc[o.key] = o.label;
  return acc;
}, {});

function LangBadge({ code }) {
  const map = { de: "🇩🇪 DE", en: "🇬🇧 EN", bks: "🌍 BKS" };
  return <span className="lang-badge">{map[code] || code}</span>;
}

// Data + auth logic for the admin dashboard.
function useAdminData() {
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (pw) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Password": pw };
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/signups/stats`, { headers }),
        axios.get(`${API}/signups`, { headers }),
      ]);
      setStats(statsRes.data);
      setSignups(listRes.data);
      setAuthed(true);
      sessionStorage.setItem("zubo_admin_pw", pw);
    } catch (err) {
      setAuthed(false);
      sessionStorage.removeItem("zubo_admin_pw");
      setLoginError("Falsches Passwort oder Serverfehler.");
    } finally {
      setLoading(false);
    }
  }, [setLoading, setStats, setSignups, setAuthed, setLoginError]);

  useEffect(() => {
    const saved = sessionStorage.getItem("zubo_admin_pw");
    if (saved) loadData(saved);
  }, [loadData]);

  const logout = useCallback(() => {
    sessionStorage.removeItem("zubo_admin_pw");
    setAuthed(false);
    setStats(null);
    setSignups([]);
  }, [setAuthed, setStats, setSignups]);

  return { authed, loginError, setLoginError, stats, signups, loading, loadData, logout };
}

async function downloadCsv() {
  try {
    const res = await axios.get(`${API}/signups/export.csv`, {
      headers: { "X-Admin-Password": sessionStorage.getItem("zubo_admin_pw") },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "zubo_signups.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    alert("CSV-Export fehlgeschlagen.");
  }
}

function AdminLogin({ onLogin, loading, error }) {
  const [password, setPassword] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(password);
  };
  return (
    <div className="admin-login-wrap">
      <form className="admin-login-card" onSubmit={handleSubmit}>
        <h1>ZUBO Admin</h1>
        <p>Geschützter Bereich — bitte Passwort eingeben.</p>
        <input
          type="password"
          placeholder="Passwort"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        <button type="submit" data-testid="admin-login-button" disabled={loading}>{loading ? "…" : "Anmelden"}</button>
        {error && <p className="admin-login-error" data-testid="admin-login-error">{error}</p>}
      </form>
    </div>
  );
}

function StatCards({ stats }) {
  const byLang = stats?.by_lang || {};
  return (
    <div className="stat-cards">
      <div className="stat-card">
        <span className="stat-num" data-testid="stat-total">{stats?.total ?? 0}</span>
        <span className="stat-label">Anmeldungen gesamt</span>
      </div>
      <div className="stat-card accent">
        <span className="stat-num" data-testid="stat-preorders">{stats?.preorders ?? 0}</span>
        <span className="stat-label">Buch-Vorbestellungen</span>
      </div>
      <div className="stat-card">
        <span className="stat-num">
          {(byLang.de || 0)} / {(byLang.en || 0)} / {(byLang.bks || 0)}
        </span>
        <span className="stat-label">Sprache DE / EN / BKS</span>
      </div>
    </div>
  );
}

function FeatureBars({ stats }) {
  const featureCounts = stats?.feature_counts || {};
  const maxFeature = Math.max(1, ...Object.values(featureCounts));
  const orderedFeatures = FEATURE_OPTIONS.de
    .map((o) => ({ key: o.key, label: o.label, count: featureCounts[o.key] || 0 }))
    .sort((a, b) => b.count - a.count);
  return (
    <section className="admin-panel">
      <h2>Beliebteste Funktionen</h2>
      <div className="bars">
        {orderedFeatures.map((f) => (
          <div className="bar-row" key={f.key}>
            <span className="bar-label">{f.label}</span>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${(f.count / maxFeature) * 100}%` }} />
            </div>
            <span className="bar-count">{f.count}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SignupsTable({ signups }) {
  return (
    <section className="admin-panel">
      <h2>Alle Anmeldungen ({signups.length})</h2>
      <div className="table-scroll">
        <table className="admin-table" data-testid="signups-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>E-Mail</th>
              <th>Sprache</th>
              <th>Vorbestellung</th>
              <th>Gewünschte Funktionen</th>
            </tr>
          </thead>
          <tbody>
            {signups.length === 0 && (
              <tr><td colSpan="5" className="empty">Noch keine Anmeldungen.</td></tr>
            )}
            {signups.map((s) => (
              <tr key={s.id}>
                <td>{new Date(s.created_at).toLocaleString("de-AT")}</td>
                <td>{s.email}</td>
                <td><LangBadge code={s.lang} /></td>
                <td>{s.preorder_book ? "✅ Ja" : "—"}</td>
                <td>
                  <div className="feat-tags">
                    {(s.features || []).map((f) => (
                      <span className="feat-tag" key={f}>{FEATURE_LABELS[f] || f}</span>
                    ))}
                    {(!s.features || s.features.length === 0) && <span className="muted">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AdminDashboard() {
  const { authed, loginError, setLoginError, stats, signups, loading, loadData, logout } = useAdminData();

  if (!authed) {
    return (
      <AdminLogin
        loading={loading}
        error={loginError}
        onLogin={(pw) => { setLoginError(""); loadData(pw); }}
      />
    );
  }

  return (
    <div className="admin-wrap">
      <header className="admin-header">
        <div className="admin-title">
          <img src="/images/mascot-badge-v2.jpg" alt="Zubo" className="admin-mascot" />
          <div>
            <h1>ZUBO Dashboard</h1>
            <span>Anmeldungen & Feature-Wünsche</span>
          </div>
        </div>
        <div className="admin-actions">
          <button className="btn-csv" data-testid="csv-export-button" onClick={downloadCsv}>⬇ CSV exportieren</button>
          <button className="btn-logout" data-testid="admin-logout-button" onClick={logout}>Abmelden</button>
        </div>
      </header>
      <StatCards stats={stats} />
      <FeatureBars stats={stats} />
      <SignupsTable signups={signups} />
    </div>
  );
}
